import {Avatar, AvatarModule} from 'primeng/avatar';
import {AvatarGroupModule} from 'primeng/avatargroup';
import {Component, computed, effect, inject, input, linkedSignal, signal} from '@angular/core';
import {UserPortrait, MinimalUserModel} from '@core/api/data-access';
import {UserStore} from '@data-users';
import {StoredFile} from '@global-data';
import {take} from 'rxjs';
import {MatTooltip} from '@angular/material/tooltip';

type IUserPortraitData = {
	id: string;
	file?: StoredFile;
	user?: MinimalUserModel;
	displayName?: string;
	initials?: string;
	notificationSrc?: 'fad fa-at' | 'fad fa-mobile-alt' | 'fab fa-whatsapp' | 'fad fa-qrcode';
};
/* -------------------------------------------------------------------------------------------------------------- */
@Component({
	selector: 'dc-avatars',
	templateUrl: './avatars.component.html',
	styleUrls: ['./avatars.component.scss'],
	imports: [AvatarGroupModule, MatTooltip, AvatarModule],
})
export class AvatarsComponent {
	/* ------------------------------------------  Types and Constants ------------------------------------------ */

	/* ------------------------------------------ PROVIDERS / SERVICES ------------------------------------------ */
	protected readonly userStore = inject(UserStore);

	/* ------------------------------------------------  Inputs ------------------------------------------------ */
	public readonly userModels = input<MinimalUserModel[]>();
	private readonly validUserModels = computed(() => this.userModels()?.filter(Boolean) || []);
	readonly size = input<Avatar['size']>('normal');
	public readonly blockEffects = input<boolean>(false);
	public readonly showTooltip = input<boolean>(true);
	public readonly invisible = input<boolean>(false);
	public readonly avatarStyleClass = input<string>('');
	public readonly avatarClass = input<string>('');
	public readonly containerStyleClass = input<string>('');
	/* ------------------------------------------------  Outputs ------------------------------------------------ */

	/* ------------------------------------------------  Signals ------------------------------------------------ */
	private readonly userPortraits$ = signal<Record<string, UserPortrait>>(null);
	public readonly portraits$ = computed<IUserPortraitData[]>(() => {
		const userPortraits = this.userPortraits$();
		const users = this.validUserModels() || [];
		if (!userPortraits) return [];

		// Create a map of all users first
		const userMap = new Map(users.map(user => [user.id, user]));
		const portraits: IUserPortraitData[] = [];

		// Process all users that exist in userPortraits
		Object.entries(userPortraits).forEach(([userId, portrait]) => {
			const user = userMap.get(userId);
			if (!user) return;
			const displayName = user.fullname || user.phonenumber || user.email || user.username || '';

			const initials = user.fullname
				?.split(' ')
				.map(word => word?.[0])
				.join('')
				.toUpperCase();

			let notificationSrc: 'fad fa-at' | 'fad fa-mobile-alt' | 'fab fa-whatsapp' | 'fad fa-qrcode' | undefined;
			if (!portrait?.image) {
				const preferences = user.preferences;
				notificationSrc = preferences?.notifyEmail ? 'fad fa-at' : preferences?.notifySMS ? 'fad fa-mobile-alt' : preferences?.notifyWhatsapp ? 'fab fa-whatsapp' : 'fad fa-qrcode';
			}

			portraits.push({
				id: userId,
				file: portrait?.image ? new StoredFile(portrait.image, portrait.imageType) : undefined,
				user,
				initials,
				notificationSrc,
				displayName,
			});
		});

		// Add remaining users without portraits
		users.forEach(user => {
			if (portraits.some(p => p.id === user.id)) return;

			const initials = user.fullname
				?.split(' ')
				.map(word => word?.[0])
				.join('')
				.toUpperCase();

			const preferences = user.preferences;
			const notificationSrc = preferences?.notifyEmail ? 'fad fa-at' : preferences?.notifySMS ? 'fad fa-mobile-alt' : preferences?.notifyWhatsapp ? 'fab fa-whatsapp' : 'fad fa-qrcode';
			const displayName = user.fullname || user.phonenumber || user.email || user.username || '';

			portraits.push({
				id: user.id,
				user,
				initials,
				notificationSrc,
				displayName,
			});
		});

		return portraits;
	});

	public readonly loading$ = linkedSignal(() => {
		const portraits = this.portraits$();
		if (!portraits) return true;
		return false;
	});

	/* -------------------------------------------------- Data -------------------------------------------------- */

	/* ------------------------------------------------  Constructor ------------------------------------------------ */
	constructor() {
		const loadPortraitsEffectRef$ = effect(() => {
			const userModels = this.validUserModels();
			if (!userModels) return;
			if (userModels?.length) {
				const usersWithPortrait: string[] = [...new Set(userModels.flatMap(user => (user.hasPortrait === true ? user.id : [])))];
				this.userStore
					.loadUserPortrait(...usersWithPortrait)
					.pipe(take(1))
					.subscribe(response => {
						this.userPortraits$.set(response);
						this.loading$.set(false);
					});
			} else {
				this.userPortraits$.set({});
				this.loading$.set(false);
			}
			loadPortraitsEffectRef$.destroy();
		});
	}

	/* ----------------------------------------------- Lifecycle Hooks ----------------------------------------------- */

	/* ------------------------------------------------  Methods ------------------------------------------------ */
}
